import { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import OldTooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import Tooltip from '@picsart/rc/Tooltip';
import { useTranslator } from '@picsart/rc/services/localization';

import CategoryButton from 'components/common/CategoryButton';

import { useSidebarStyles } from '../styles';
import { EraserToolsEnum, IEraserTool } from '../config';

interface IEraserToolProps {
  active: boolean;
  tool: IEraserTool;
  disabled?: boolean;
  onToolSelect: (type: EraserToolsEnum) => void;
  isDarkMode: boolean;
}

const EraserTool = ({
  tool,
  active,
  onToolSelect,
  disabled = false,
  isDarkMode,
}: IEraserToolProps) => {
  const classes = useSidebarStyles({
    isDarkMode,
  });
  const translate = useTranslator();

  const handleSelect = useCallback(() => {
    !disabled && onToolSelect(tool.type);
  }, [disabled, onToolSelect, tool.type]);

  const isHover = useMemo(() => !disabled && !active, [disabled, active]);
  const { icon: Icon } = tool;

  const tooltipContent = (
    <div
      className={classNames(classes.sidebarTools, classes.mainSettings, {
        [classes.disabledBrush]: disabled,
        [classes.hoverToolContainer]: isHover,
        [classes.hoverCutoutContainer]: isHover && tool.type === EraserToolsEnum.CUT_OUT,
      })}
      onClick={handleSelect}
    >
      <div
        id={`${tool.type}-id`}
        className={classes.toolContainer}
      >
        {active && <div className={classes.sidebarCategoryIndicator} />}
        <Icon className={classNames(classes.toolIcons, { [classes.activeToolIcon]: active })} />
        <p
          className={classNames(classes.toolIconName, classes.mainSettings, {
            [classes.deselectedTool]: !active,
          })}
        >
          {translate(tool.name)}
        </p>
      </div>
    </div>
  );

  if (!isDarkMode) {
    return (
      <OldTooltip
        delayShow={1000}
        offset={[10, -10]}
        content={translate(tool.name)}
        placement={placementsList.bottom}
      >
        {tooltipContent}
      </OldTooltip>
    );
  }

  return (
    <div id={`${tool.type}-id`} className={classes.sidebarTooltipItem}>
      <Tooltip
        enableArrow={false}
        mouseEnterDelay={1000}
        mouseLeaveDelay={1000}
        offset={[10, 15]}
        overlay={<p>{translate(tool.name)}</p>}
        placement="bottom"
      >
        <span>
          <CategoryButton
            isActive={active}
            isDisabled={disabled}
            icon={Icon}
            textContent={translate(tool.name)}
            tabIndex={0}
            onClick={handleSelect}
            isMultiLineTruncated
          />
        </span>
      </Tooltip>
    </div>
  );
};

export default EraserTool;
